import React, { useCallback, useEffect } from 'react';
import { Box, Checkbox, Grid, Typography, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomDatePicker from '../CustomDatePicker';
import { useFormik } from 'formik';
import { OrderCheckoutSubjectsSchema } from '../validations/orderValidations';
import PrimaryBlueButton from '../components/common/buttons/PrimaryBlueButton';
import { colors } from '../styles/fontStyles';
import { Link } from 'react-router-dom';
import OrderCheckoutProductImage from './OrderCheckoutProductImage';
import { validatePhoneNumber } from '../utils/validations';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import { UNAUTHENTICATED } from '../constants/user';
import { getCheckoutUrl } from './utils';

type SubjectProps = {
  name: string;
  dob: string;
  phoneNumber: string;
  isAnAdult: boolean;
};

type OrderSubjectsFormProps = {
  quantity: number;
  subjects: SubjectProps[];
  setSubjects: (subjects: SubjectProps[]) => void;
  updateApiCartWithCallbacks: (
    cartId: string,
    shopifyCheckoutUrl: string,
    cartData: any,
    successCallback: any,
    failureCallback: any,
  ) => void;
  goToShippingSubmitting: boolean;
  setGoToShippingSubmitting: any;
  goToShippingEnabled: boolean;
  setGoToShippingEnabled: any;
  setSuccessGoToShippingBtn: any;
  setFailureGoToShippingBtn: any;
  cart: string;
};

type FormikValues = {
  subjects: SubjectProps[];
};

const GridContainerSubjects = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    flexDirection: 'column',
    textAlign: 'center',
  },
}));

export default function OrderSubjectsForm({
  quantity = 1,
  subjects,
  setSubjects,
  updateApiCartWithCallbacks,
  goToShippingSubmitting,
  setGoToShippingSubmitting,
  goToShippingEnabled,
  setGoToShippingEnabled,
  setSuccessGoToShippingBtn,
  setFailureGoToShippingBtn,
  cart,
}: OrderSubjectsFormProps) {

  const { userType, theme } = useThemeContext();
  const isUserGuest = userType === UNAUTHENTICATED;

  const formik = useFormik<FormikValues>({
    initialValues: {
      subjects,
    },
    validationSchema: OrderCheckoutSubjectsSchema,
    onSubmit: (values) => {
      handleSubjectsValidation(values);
    },
  });

  const handleSubjectsValidation = useCallback(
    async (formikValues: { subjects: SubjectProps[] }) => {
      setGoToShippingSubmitting(true);
      const newSubjects = formikValues.subjects;
      let phoneNumber = newSubjects[0].phoneNumber;
      phoneNumber = phoneNumber.startsWith('+1') ? phoneNumber : `+1${phoneNumber}`;

      // Validate phone number of the subject
      const isPhoneValid = await validatePhoneNumber(phoneNumber);

      if (!isPhoneValid) {
        setGoToShippingEnabled(false);
        setGoToShippingSubmitting(false);
        formik.setFieldError(`subjects[0].phoneNumber`, 'Invalid phone number');
        console.error('Phone number validation failed.');
      }

      const currentSubject = newSubjects[0];
      const newSubject = { ...currentSubject, phoneNumber: phoneNumber };
      setSubjects([newSubject]);

      if (!cart || cart.length === 0) {
        return;
      }
      let sCUrl = await getCheckoutUrl(cart);
      if (!sCUrl) {
        return; // this happens when the cart has been checked out already (cart is deleted from shopify)
      }

      const cartData = {
        quantity: quantity,
        subjects: [newSubject],
      };
      updateApiCartWithCallbacks(cart, sCUrl, cartData, setSuccessGoToShippingBtn, setFailureGoToShippingBtn);
    },
    [updateApiCartWithCallbacks, setSubjects, setGoToShippingEnabled, setGoToShippingSubmitting, cart],
  );

  const customNameChange = (e: React.ChangeEvent<HTMLInputElement>, num: number) => {
    const updatedName = e.target.value;
    formik.setFieldValue(`subjects[${num}].name`, updatedName);
    const currentSubject = subjects[num];
    const newSubject = { ...currentSubject, name: updatedName };
    setSubjects([newSubject]);
  };

  const customPhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>, num: number) => {
    const updatedPhoneNumber = e.target.value;
    formik.setFieldValue(`subjects[${num}].phoneNumber`, updatedPhoneNumber);
    const currentSubject = subjects[num];
    const newSubject = {
      ...currentSubject,
      phoneNumber: updatedPhoneNumber.startsWith('+1') ? updatedPhoneNumber : `+1${updatedPhoneNumber}`,
    };
    setSubjects([newSubject]);
  };

  const customDobChange = (currentDate: string, num: number) => {
    formik.setFieldValue(`subjects[${num}].dob`, currentDate);
    const currentSubject = subjects[num];
    const newSubject = { ...currentSubject, dob: currentDate };
    setSubjects([newSubject]);
  };

  const handleAdultsOnlyChange = (event: React.ChangeEvent<HTMLInputElement>, num: number) => {
    formik.setFieldValue(`subjects[${num}].isAnAdult`, event.target.checked);
    const currentSubject = subjects[num];
    const newSubject = { ...currentSubject, isAnAdult: event.target.checked };
    setSubjects([newSubject]);
  };

  useEffect(() => {
    const areAllFieldsFilled = formik.values.subjects.every(
      (subject) => subject.name && subject.phoneNumber && subject.dob && subject.isAnAdult !== undefined,
    );

    if (formik.isValid && areAllFieldsFilled && !formik.isValidating) {
      goToShippingEnabled = true;
      setGoToShippingEnabled(true);
    } else {
      setGoToShippingEnabled(false);
    }
  }, [formik.isValid, formik.isValidating, formik.values, setGoToShippingEnabled]);

  const renderAdultsOnlyMessage = (num: number) => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: 'row',
          background: theme.custom.orderPFAS.subjects.adultsOnlyMessage.background,
          borderRadius: '10px',
          padding: '16px',
          margin: '16px 0',
        }}
      >
        <Checkbox
          checked={Boolean(formik.values.subjects[num]?.isAnAdult)}
          name={`subjects[${num}].isAnAdult`}
          size="small"
          onChange={(event) => handleAdultsOnlyChange(event, num)}
          sx={{
            padding: 0,
            marginTop: '2px',
            marginRight: '8px',
            alignSelf: 'flex-start',
            color: theme.custom.orderPFAS.subjects.adultsOnlyMessage.checkbox.color,
            '&.Mui-checked': {
              color: theme.custom.orderPFAS.subjects.adultsOnlyMessage.checkbox.colorChecked,
            },
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: '6px' }}>
          <Typography
            sx={{
              color: theme.custom.orderPFAS.subjects.adultsOnlyMessage.color,
              fontSize: '14px',
              fontWeight: '500',
              textAlign: 'left',
              marginBottom: '8px',
            }}
          >
            Tests are currently validated for 18 and over. Please ensure that everyone taking the test is at least 18
            years of age.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.custom.orderPFAS.subjects.adultsOnlyMessage.colorTos,
              fontSize: '12px',
            }}
          >
            If needed, please read our{' '}
            <Link to="/tos.html" target="_blank">
              <span style={{ fontWeight: 'bold', color: theme.custom.orderPFAS.subjects.adultsOnlyMessage.colorTosLink }}>Terms of Service</span>
            </Link>
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderPhoneNumberGuestValidation = (num: number) => {
    if (!isUserGuest) return null;
    return (
      <Grid item xs={12} md={12}>
        <TextField
          sx={{ width: '100%', mb: 3 }}
          id={`subjects[${num}].phoneNumber`}
          name={`subjects[${num}].phoneNumber`}
          label="Phone Number"
          value={formik.values.subjects[num]?.phoneNumber || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => customPhoneNumberChange(e, num)}
          onBlur={formik.handleBlur}
          error={Boolean(
            formik.touched.subjects?.[num]?.phoneNumber && (formik.errors.subjects as any)?.[num]?.phoneNumber,
          )}
          helperText={
            formik.touched.subjects?.[num]?.phoneNumber && (formik.errors.subjects as any)?.[num]?.phoneNumber
              ? `${(formik.errors.subjects as any)?.[num]?.phoneNumber}`
              : 'Please enter phone number'
          }
          required
        />
      </Grid>
    );
  };

  const renderSubjectsForm = () => {
    return (
      <>
        {Array.from({ length: quantity }, (_, i) => i).map((num) => (
          <React.Fragment key={'subject-' + num}>
            <Grid item xs={12} md={12}>
              <TextField
                sx={{ width: '100%', mb: 3 }}
                id={`subjects[${num}].name`}
                name={`subjects[${num}].name`}
                label="Name of person taking the test"
                value={formik.values.subjects[num]?.name || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => customNameChange(e, num)}
                onBlur={formik.handleBlur}
                FormHelperTextProps={{
                  ...(formik.touched.subjects?.[num]?.name && (formik.errors.subjects as any)?.[num]?.name),
                  sx: {
                    fontSize: '12',
                    fontWeigth: 400,
                  },
                }}
                error={Boolean(formik.touched.subjects?.[num]?.name && (formik.errors.subjects as any)?.[num]?.name)}
                helperText={
                  formik.touched.subjects?.[num]?.name && (formik.errors.subjects as any)?.[num]?.name
                    ? `${(formik.errors.subjects as any)?.[num]?.name}`
                    : 'Please enter your full name'
                }
                required
              />
            </Grid>
            {renderPhoneNumberGuestValidation(num)}
            <Grid item xs={12} md={12}>
              <CustomDatePicker
                DatePickerProps={{ required: true }}
                value={formik.values.subjects[num]?.dob || null}
                onChange={(newValue: string | null) => {
                  if (newValue === null) {
                    formik.setFieldTouched(`subjects[${num}].dob`, true);
                    formik.setFieldValue(`subjects[${num}].dob`, '', true);
                    formik.setFieldError(`subjects[${num}].dob`, 'Birthdate is required');
                  } else {
                    formik.setFieldTouched(`subjects[${num}].dob`, true);
                    formik.setFieldValue(`subjects[${num}].dob`, newValue, true);
                    formik.setFieldError(`subjects[${num}].dob`, '');
                    customDobChange(newValue, num);
                  }
                }}
                label="Birthdate"
                TextFieldProps={{
                  helperText:
                    formik.touched.subjects?.[num]?.dob && (formik.errors.subjects as any)?.[num]?.dob
                      ? `${(formik.errors.subjects as any)?.[num]?.dob}`
                      : 'When you order a test, we need to check your date of birth.',
                  error: Boolean(formik.touched.subjects?.[num]?.dob && (formik.errors.subjects as any)?.[num]?.dob),
                  FormHelperTextProps: {
                    sx: { fontSize: '12px', fontWeight: 400 },
                  },
                }}
                error={Boolean(formik.touched.subjects?.[num]?.dob && (formik.errors.subjects as any)?.[num]?.dob)}
                helperText={
                  formik.touched.subjects?.[num]?.dob && (formik.errors.subjects as any)?.[num]?.dob
                    ? (formik.errors.subjects as any)?.[num]?.dob
                    : 'When you order a test, we need to check your date of birth.'
                }
              />
            </Grid>
            {renderAdultsOnlyMessage(num)}
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <GridContainerSubjects container spacing={2}>
      <OrderCheckoutProductImage />
      <Grid item xs={12} sm={12} md={7} style={{ paddingLeft: '33px' }}>
        <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            padding: { xs: '0', sm: '0 14%', md: '0' },
          }}
        >
          <Typography gutterBottom sx={{ typography: { sm: 'h5', md: 'h3' }, display: { xs: 'none', md: 'flex' } }}>
            Buy home PFAS test
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
          <Typography
            variant="h5"
            sx={{
              mt: 1,
              mb: 3,
              background: theme.custom.orderPFAS.subheader.background,
              color: theme.custom.orderPFAS.subheader.color,
              width: 'fit-content',
              margin: { md: '0 0 17px', sm: '0 14% 21px 14%' },
            }}
            gutterBottom
          >
            Who is this test for?
          </Typography>
        </Box>
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            padding: { md: '0 94px 0 0', sm: '0 14%', xs: '0' },
          }}
        >
          {renderSubjectsForm()}
        </Grid>

        <Box
          sx={{
            mt: 2,
            padding: { xs: '0', sm: '0 14%', md: '0' },
          }}
        >
          <PrimaryBlueButton
            onClick={() => {
              if (!goToShippingSubmitting) {
                formik.handleSubmit(); // Prevent click when submitting data.
              }
            }}
            buttonText="Go to shipping"
            sxProps={{ mr: 1, width: { xs: '100%', md: 'auto' } }}
            additionalProps={{ disabled: !goToShippingEnabled || goToShippingSubmitting, type: 'submit' }}
            isLoading={goToShippingSubmitting}
          />
        </Box>
        </form>
      </Grid>
    </GridContainerSubjects>
  );
}
